import { StandaloneAddOn } from '@/hooks/useAddOns';
import { ReviewStartingKit, UpcomingKit } from '@/hooks/useOrderReview';
import { PaymentOptionKit } from '@/hooks/usePaymentOptions';
import { PaymentOptionExtend } from '@/components/common/types';
import { PaymentOption } from '@/hooks/usePaymentOptions';

import { formatMoney } from '@/utils/helpers';
import interpolate from '@/utils/translations/interpolate';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';
import { SubFlowV3ContextData } from '@/components/subflow-v3/context/SubFlowV3Context';

export const isPayPerKitOption = (size: number) => {
  return size === 1;
};

export const isBigPufOption = (size: number) => {
  return size >= 4;
};

export const getBundleTitle = (
  isBookBundleOption: boolean,
  size: number,
  t: TypeSubFlowV3Translations,
) => {
  if (size < 2) return t.paymentOptionsPerKit;

  if (isBookBundleOption) return t.paymentOptionsPrepayWithBooksHeading;

  return interpolate(t.paymentOptionsPrepayBig, {
    size,
  });
};

// determines the supporting copy to use in bulleted list within the subflow payment option radio selections
export const getSavingsLabel = ({
  isBfcm2024,
  subSquadEnableBigPuf,
  subSquadEnableDiscountDollarSavings,
  size,
  t,
  savings,
  locale,
}: {
  isBfcm2024?: string | boolean;
  subSquadEnableBigPuf?: string | boolean;
  subSquadEnableDiscountDollarSavings?: boolean;
  size: number;
  t: TypeSubFlowV3Translations;
  savings: number;
  locale: string;
}) => {
  const isPayPerKit = isPayPerKitOption(size);
  const isBigPuf = isBigPufOption(size);

  if (isPayPerKit) {
    return t.ppkText;
  }

  if (isBfcm2024 && !isPayPerKit) {
    return t.bfcm2024PaymentOptionsSavingsLabel;
  }

  let discount = subSquadEnableBigPuf && isBigPuf ? '10%' : '5%';

  if (subSquadEnableDiscountDollarSavings) {
    discount = formatMoney(savings / 100, locale, true);
  }

  return interpolate(t.paymentOptionSavingsLabel, {
    discount,
  });
};

export const hasMultiplePrices = (
  kits: Array<
    PaymentOptionKit | UpcomingKit | ReviewStartingKit | StandaloneAddOn
  >,
) => {
  if (kits.length === 0) return false;

  const currentPrice = kits[0].price;

  return kits.slice(1).some((kit) => {
    if (kit.price !== currentPrice) {
      return true;
    }

    return false;
  });
};

export const shouldShowBestDeal = (
  option: PaymentOption | PaymentOptionExtend,
  allOptions?: (PaymentOption | PaymentOptionExtend)[],
): boolean => {
  if (!allOptions || allOptions.length <= 1) return false;
  const optionsWithBadges = allOptions.filter((opt) => opt.badge_title);
  if (optionsWithBadges.length <= 1) return false;

  const bestOption = optionsWithBadges.reduce((best, current) => {
    // Prioritize by savings amount
    if (current.savings > best.savings) return current;
    if (current.savings < best.savings) return best;

    // If savings are equal, prioritize by bundle size
    if (current.size > best.size) return current;

    return best;
  });

  return bestOption.uuid === option.uuid;
};

export function getDefaultPaymentOption(data: SubFlowV3ContextData) {
  const previouslySelectedOption = data.paymentOption;

  const firstOption = data.paymentOptions?.payment_options?.[0];

  const defaultOption = data.paymentOptions?.payment_options?.find(
    (option) => option.is_default === 'true',
  );

  return previouslySelectedOption || defaultOption || firstOption || null;
}
